<template>
  <div
    class="ai-cli-msg"
    :class="`ai-cli-msg--${message.role}`"
  >
    <div
      v-if="message.role === 'user'"
      class="ai-cli-msg__user"
    >
      <span class="ai-cli-msg__prompt">&gt;</span>
      <span class="ai-cli-msg__user-text">{{ message.text }}</span>
      <span class="ai-cli-msg__time">{{ formatTime(message.at) }}</span>
    </div>

    <div
      v-else
      class="ai-cli-msg__assistant"
    >
      <div
        v-if="message.plan"
        class="ai-cli-msg__block"
      >
        <span class="ai-cli-msg__tag ai-cli-msg__tag--plan">[plan]</span>
        <span class="ai-cli-msg__block-text">{{ message.plan }}</span>
      </div>

      <div
        v-for="tool in message.tools"
        :key="tool.id"
        class="ai-cli-msg__block ai-cli-msg__tool"
      >
        <button
          type="button"
          class="ai-cli-msg__tool-head"
          :aria-expanded="isToolOpen(tool.id) ? 'true' : 'false'"
          @click="toggleTool(tool.id)"
        >
          <span
            class="ai-cli-msg__tag"
            :class="tool.status === 'running' ? 'ai-cli-msg__tag--running' : 'ai-cli-msg__tag--ok'"
          >
            {{ tool.status === 'running' ? '[run ]' : '[ok  ]' }}
          </span>
          <code class="ai-cli-msg__tool-name">{{ tool.name }}</code>
          <span
            v-if="tool.status === 'running'"
            class="ai-cli-msg__spinner"
          />
          <span class="ai-cli-msg__tool-meta">{{ isToolOpen(tool.id) ? 'hide' : 'details' }}</span>
          <span
            class="ai-cli-msg__chev"
            :class="{ 'ai-cli-msg__chev--open': isToolOpen(tool.id) }"
          >›</span>
        </button>
        <template v-if="isToolOpen(tool.id)">
          <pre class="ai-cli-msg__json ai-cli-msg__json--args">{{ formatJson(tool.args) }}</pre>
          <pre
            v-if="tool.result !== undefined"
            class="ai-cli-msg__json ai-cli-msg__json--result"
          >{{ formatJson(tool.result) }}</pre>
        </template>
      </div>

      <div
        v-if="message.answer"
        class="ai-cli-msg__block"
      >
        <span class="ai-cli-msg__tag ai-cli-msg__tag--answer">[ans ]</span>
        <RenderedAnswer
          class="ai-cli-msg__answer"
          :text="message.answer"
        />
      </div>

      <div
        v-if="chartData"
        class="ai-cli-msg__block"
      >
        <span class="ai-cli-msg__tag ai-cli-msg__tag--link">[chart]</span>
        <div class="ai-cli-msg__chart-wrap">
          <button
            type="button"
            class="ai-cli-msg__chart-toggle"
            @click="showChart = !showChart"
          >
            {{ showChart ? 'hide chart' : 'view as chart' }}
          </button>
          <AiAgentChart
            v-if="showChart"
            :data="chartData"
          />
        </div>
      </div>

      <div
        v-if="message.status === 'streaming' && !message.answer && !message.tools.length && !message.plan"
        class="ai-cli-msg__block"
      >
        <span class="ai-cli-msg__tag ai-cli-msg__tag--plan">[...]</span>
        <span class="ai-cli-msg__block-text ai-cli-msg__pulse">waiting</span>
      </div>

      <div
        v-if="message.status === 'error'"
        class="ai-cli-msg__block ai-cli-msg__error"
      >
        <span class="ai-cli-msg__tag ai-cli-msg__tag--err">[err ]</span>
        <span class="ai-cli-msg__block-text">{{ message.error || 'unknown error' }}</span>
      </div>

      <div
        v-if="message.status === 'done' || message.status === 'error'"
        class="ai-cli-msg__block ai-cli-msg__done"
      >
        <span class="ai-cli-msg__tag ai-cli-msg__tag--done">[done]</span>
        <span class="ai-cli-msg__block-text">
          {{ duration }} · {{ message.tools.length }} tool {{ message.tools.length === 1 ? 'call' : 'calls' }}
        </span>
      </div>

      <div
        v-if="message.citations.length"
        class="ai-cli-msg__block"
      >
        <span class="ai-cli-msg__tag ai-cli-msg__tag--link">[link]</span>
        <CitationLinks
          :items="message.citations"
          container-class="ai-cli-msg__links"
          link-class="ai-cli-msg__link"
        />
      </div>
    </div>
  </div>
</template>

<script>
import RenderedAnswer from './RenderedAnswer.vue'
import CitationLinks from './CitationLinks.vue'
import AiAgentChart from './AiAgentChart.vue'
import { isValidMessage } from './message-shape'
import { extractChartData } from './chartable'

export default {
  name: 'AiAgentCliMessage',

  components: { RenderedAnswer, CitationLinks, AiAgentChart },

  props: {
    message: {
      type: Object,
      required: true,
      validator: isValidMessage
    }
  },

  data() {
    return {
      showChart: false,
      // Tool args/results are collapsed by default to keep the transcript
      // scannable; an admin expands a specific call on demand. Keyed by
      // tool id so each call remembers its own open/closed state.
      expandedTools: {}
    }
  },

  computed: {
    // Offer a chart only once the answer is finalized, so a half-streamed
    // table never parses. Returns null when nothing chartable is found.
    chartData() {
      if (this.message.role !== 'assistant' || this.message.status === 'streaming') {
        return null
      }

      return extractChartData(this.message.answer)
    },

    // Computed (not a method) so it only recomputes when message.at or
    // message.lastAt actually change. Previously this was a method that
    // fell back to Date.now() on every render — the displayed duration
    // would creep up as the panel re-rendered for unrelated reasons.
    duration() {
      const msg = this.message
      if (!msg.at) {
        return ''
      }

      // While streaming we have no end yet; show a placeholder rather
      // than a constantly-growing number. The store stamps lastAt the
      // moment status flips to 'done' or 'error'.
      if (!msg.lastAt) {
        return '…'
      }

      const ms = Math.max(0, msg.lastAt - msg.at)
      if (ms < 1000) {
        return `${ms}ms`
      }

      return `${(ms / 1000).toFixed(1)}s`
    }
  },

  methods: {
    isToolOpen(id) {
      return Boolean(this.expandedTools[id])
    },
    toggleTool(id) {
      this.$set(this.expandedTools, id, !this.expandedTools[id])
    },
    formatTime(ts) {
      if (!ts) {
        return ''
      }

      const d = new Date(ts)
      return d.toLocaleTimeString([], { hour12: false })
    },
    formatJson(value) {
      try {
        return JSON.stringify(value, null, 2)
      } catch (e) {
        return String(value)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.ai-cli-msg {
  font-family: 'SF Mono', 'JetBrains Mono', 'Menlo', 'Consolas', monospace;
  font-size: 0.78rem;
  line-height: 1.55;
  color: #cdd6e3;

  & + & {
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px dashed rgba(143, 166, 200, 0.18);
  }

  &__user {
    display: flex;
    align-items: baseline;
    gap: 8px;
    color: #ffffff;
  }

  &__prompt {
    color: #6ee7b7;
    font-weight: 700;
    flex-shrink: 0;
  }

  &__user-text {
    flex: 1;
    word-break: break-word;
    white-space: pre-wrap;
  }

  &__time {
    color: #6b7fa8;
    font-size: 0.7rem;
    flex-shrink: 0;
  }

  &__assistant {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: 6px;
    // Let children shrink instead of forcing the panel wider than the viewport
    // (the classic flexbox min-width:auto overflow that caused a second
    // horizontal scrollbar).
    min-width: 0;
  }

  &__block {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    min-width: 0;
  }

  &__block-text {
    flex: 1;
    min-width: 0;
    word-break: break-word;
    white-space: pre-wrap;
  }

  &__tag {
    color: #6b7fa8;
    flex-shrink: 0;
    user-select: none;
    font-weight: 600;

    &--plan   { color: #c4b5fd; }
    &--running { color: #fbbf24; }
    &--ok     { color: #6ee7b7; }
    &--answer { color: #7ea9f5; }
    &--err    { color: #f87171; }
    &--done   { color: #6b7fa8; }
    &--link   { color: #c4b5fd; }
  }

  &__tool {
    flex-direction: column;
    gap: 4px;
    padding-left: 0;
    min-width: 0;
  }

  &__tool-head {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    min-width: 0;
    appearance: none;
    background: transparent;
    border: 0;
    margin: 0;
    padding: 2px 4px;
    border-radius: 4px;
    font: inherit;
    color: inherit;
    text-align: left;
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover { background: rgba(143, 166, 200, 0.08); }
  }

  &__tool-name {
    color: #ffffff;
    font-family: inherit;
    background: transparent;
    padding: 0;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__tool-meta {
    margin-left: auto;
    flex-shrink: 0;
    color: #6b7fa8;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }

  &__chev {
    flex-shrink: 0;
    color: #6b7fa8;
    display: inline-block;
    line-height: 1;
    transform: rotate(90deg);
    transition: transform 0.15s ease;

    &--open { transform: rotate(-90deg); }
  }

  &__json {
    // Full width of the message column, no big left indent, so tool payloads
    // use all the available area. The block wraps and flows into the single
    // panel scroll instead of owning its own scrollbar — these are collapsed
    // by default, so length only matters once an admin expands them.
    margin: 0;
    width: 100%;
    box-sizing: border-box;
    padding: 8px 12px;
    background: rgba(143, 166, 200, 0.06);
    border-left: 2px solid rgba(143, 166, 200, 0.25);
    border-radius: 0 4px 4px 0;
    color: #adbacf;
    font-size: 0.72rem;
    line-height: 1.45;
    white-space: pre-wrap;
    word-break: break-word;
    overflow-wrap: anywhere;

    &--args   { border-left-color: rgba(196, 181, 253, 0.5); }
    &--result { border-left-color: rgba(110, 231, 183, 0.5); }
  }

  &__answer {
    flex: 1;
    min-width: 0;
    color: #e5edf8;

    ::v-deep p {
      margin: 0 0 6px;
      &:last-child { margin-bottom: 0; }
    }
    ::v-deep strong { color: #ffffff; font-weight: 600; }
    ::v-deep code {
      background: rgba(143, 166, 200, 0.1);
      color: #6ee7b7;
      padding: 0 4px;
      border-radius: 3px;
      font-family: inherit;
    }
    ::v-deep a { color: #7ea9f5; }
    ::v-deep ul, ::v-deep ol { margin: 0 0 6px; padding-left: 18px; }
    ::v-deep table {
      border-collapse: collapse;
      width: 100%;
      margin: 6px 0;
      font-size: 0.74rem;
    }
    ::v-deep th, ::v-deep td {
      padding: 3px 8px;
      border-bottom: 1px solid rgba(143, 166, 200, 0.18);
      text-align: left;
    }
    ::v-deep th { color: #ffffff; font-weight: 600; }
  }

  &__spinner {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    border: 2px solid rgba(251, 191, 36, 0.3);
    border-top-color: #fbbf24;
    animation: ai-cli-spin 0.7s linear infinite;
  }

  &__pulse {
    color: #6b7fa8;
    animation: ai-cli-pulse 1.6s ease-in-out infinite;
  }

  &__links {
    flex: 1;
    display: flex;
    flex-wrap: wrap;
    gap: 6px 12px;
  }

  &__link {
    color: #7ea9f5;
    text-decoration: underline;
    text-underline-offset: 2px;

    &:hover { color: #c7d2f7; }
  }

  &__chart-wrap {
    flex: 1;
    min-width: 0;
  }

  &__chart-toggle {
    appearance: none;
    background: rgba(126, 169, 245, 0.12);
    border: 1px solid rgba(126, 169, 245, 0.3);
    color: #7ea9f5;
    font-family: inherit;
    font-size: 0.74rem;
    padding: 2px 10px;
    border-radius: 4px;
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover { background: rgba(126, 169, 245, 0.2); }
  }
}

@keyframes ai-cli-spin { to { transform: rotate(360deg); } }
@keyframes ai-cli-pulse {
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
}
</style>
